import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Copy, Check, Link2, Loader2, Ban, CalendarClock, Download } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
    { days: 7, label: "7 dagen" },
    { days: 30, label: "30 dagen" },
    { days: 90, label: "90 dagen" },
    { days: 365, label: "1 jaar" },
];

export default function FileShareDialog({ fileId, open, onClose }) {
    const [share, setShare] = useState(null);
    const [allowDownload, setAllowDownload] = useState(true);
    const [days, setDays] = useState(30);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open || !fileId) return;
        setShare(null);
        setCopied(false);
        api.get(`/files/${fileId}/share`)
            .then((r) => {
                setShare(r.data);
                setAllowDownload(r.data.allow_download ?? true);
            })
            .catch((e) => toast.error(formatApiError(e)));
    }, [open, fileId]);

    const link = share?.enabled && share?.token ? `${window.location.origin}/f/${share.token}` : null;

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/files/${fileId}/share`, {
                allow_download: allowDownload,
                expires_days: days,
            });
            setShare(data);
            toast.success("Deellink actief");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        setBusy(true);
        try {
            await api.delete(`/files/${fileId}/share`);
            setShare((p) => ({ ...p, enabled: false, token: null, expires_at: null }));
            toast.success("Deellink gedeactiveerd");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
        } catch {
            const el = document.createElement("textarea");
            el.value = link;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            el.remove();
        }
        setCopied(true);
        toast.success("Link gekopieerd");
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="file-share-dialog">
                <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-[#0044FF]" />
                    Bestand delen
                </DialogTitle>

                {!share ? (
                    <div className="py-8 flex justify-center text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {link && (
                            <div className="space-y-2">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                    Publieke link
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        readOnly
                                        value={link}
                                        onFocus={(e) => e.target.select()}
                                        className="font-mono text-xs"
                                        data-testid="share-link-input"
                                    />
                                    <Button
                                        onClick={copy}
                                        variant="outline"
                                        className="shrink-0 rounded-md"
                                        data-testid="share-copy-btn"
                                    >
                                        {copied ? (
                                            <Check className="w-4 h-4 text-emerald-600" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                                {share.expires_at && (
                                    <div className="flex items-center gap-1.5 text-xs text-zinc-600 font-mono">
                                        <CalendarClock className="w-3.5 h-3.5" />
                                        Geldig tot{" "}
                                        {new Date(share.expires_at).toLocaleDateString("nl-BE", {
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric",
                                        })}
                                        {share.allow_download ? " · download toegestaan" : " · alleen bekijken"}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="border-t border-zinc-200 pt-4 space-y-3">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                {link ? "Instellingen bijwerken" : "Instellingen"}
                            </div>
                            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={allowDownload}
                                    onChange={(e) => setAllowDownload(e.target.checked)}
                                    className="w-4 h-4 accent-[#0044FF]"
                                    data-testid="share-allow-download"
                                />
                                <Download className="w-4 h-4 text-zinc-500" />
                                Downloaden toestaan
                            </label>
                            <div>
                                <div className="text-xs text-zinc-600 mb-1.5">Geldigheidsperiode</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {PERIODS.map((p) => (
                                        <button
                                            key={p.days}
                                            type="button"
                                            onClick={() => setDays(p.days)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                                                days === p.days
                                                    ? "bg-[#0044FF] border-[#0044FF] text-white"
                                                    : "bg-white border-zinc-200 text-zinc-700 hover:border-[#0044FF]"
                                            }`}
                                            data-testid={`share-period-${p.days}`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2 pt-1">
                            <Button
                                onClick={save}
                                disabled={busy}
                                className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md flex-1"
                                data-testid="share-save-btn"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : link ? (
                                    "Instellingen opslaan"
                                ) : (
                                    "Maak deellink"
                                )}
                            </Button>
                            {link && (
                                <Button
                                    onClick={disable}
                                    disabled={busy}
                                    variant="outline"
                                    className="rounded-md text-red-600 border-red-200 hover:bg-red-50"
                                    data-testid="share-disable-btn"
                                >
                                    <Ban className="w-4 h-4 mr-1.5" />
                                    Deactiveer
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
